import { DeviceWithInstalledPower } from '@e2m-customer-portal/domains';
import { useLocale } from '@e2m-customer-portal/hooks';
import {
  addHours,
  eachDayOfInterval,
  eachHourOfInterval,
  endOfDay,
  getDay,
  getTime,
  getWeek,
  Interval,
  isWeekend,
  startOfDay,
  startOfWeek,
  subHours,
} from 'date-fns';
import { ForecastSlice } from '../../../../../../generated/graphql';
import clsx from 'clsx';
import { Fragment, useEffect } from 'react';
import { WarningIcon } from '../../../../../../assets/icons';
import { convertMeasure, getInputFieldTypes } from '@e2m-customer-portal/shared';
import { useTranslation } from 'react-i18next';

export interface ControlDeviceTableBodyMobileProps {
  device: DeviceWithInstalledPower;
  interval: Interval;
  slices: readonly ForecastSlice[];
}

export function ControlDeviceTableBodyMobile({
  device,
  interval,
  slices,
}: Readonly<ControlDeviceTableBodyMobileProps>) {
  const [t] = useTranslation();
  const daysInForecast = eachDayOfInterval(interval).filter((date) => getTime(date) !== getTime(interval.end));
  const { code: locale } = useLocale();
  const deviceProducts = getInputFieldTypes(device.products);

  useEffect(() => {
    const today = new Date();
    const isCurrentWeek =
      getWeek(startOfDay(today), { weekStartsOn: 1 }) === getWeek(interval.start, { weekStartsOn: 1 });
    if (isCurrentWeek) {
      setTimeout(
        () =>
          window.document
            .querySelector(`#col-${getDay(today)}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }),
        500,
      );
    } else {
      // this reset the position to monday when the interval change to another week with the table view open
      setTimeout(
        () =>
          window.document
            .querySelector(`#col-1`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' }),
        500,
      );
    }
  }, [interval]);

  return (
    <table
      className="w-full min-w-[1800px] border-spacing-0 bg-white [container:table_/_size] md:min-w-[2000px]"
      data-testid={device.name}
    >
      <colgroup>
        <col span={2} />
        {daysInForecast.map((day) => (
          <col key={`colgroup-${device.id}-${day.getDate()}`} style={{ width: `100cqw` }} />
        ))}
      </colgroup>
      <thead data-testid="control-mobile-device-table-head" className="relative">
        <tr>
          <th
            className="border-box sticky left-0 border-0 border-b border-r border-solid border-divider bg-white px-2 text-start text-sm font-normal leading-4"
            colSpan={2}
          >
            {t('MARKET_LOCATION_FORECAST_TABLE_DATE')}
          </th>
          {daysInForecast.map((date, index) => {
            return (
              <Fragment key={`date-${device.id}-${date.toISOString()}`}>
                <th
                  id={`col-${getDay(date)}`}
                  className={clsx(
                    'z-30 border border-l-0 border-t-0 border-solid border-divider py-2 text-sm font-normal leading-4 ',
                    isWeekend(date) ? 'bg-blue-grey-200/5' : 'bg-white',
                  )}
                >
                  {date
                    .toLocaleDateString(locale, {
                      weekday: 'short',
                      day: '2-digit',
                      month: '2-digit',
                    })
                    .replace(/(?<=[A-Za-z])\./, '')}
                </th>
                {index === daysInForecast.length - 1 && (
                  <th
                    className={clsx(
                      'border-0 border-b border-solid border-divider',
                      isWeekend(date) ? 'bg-blue-grey-200/5' : 'bg-white',
                    )}
                  />
                )}
              </Fragment>
            );
          })}
        </tr>
      </thead>

      <tbody data-testid="control-mobile-device-table-body">
        {eachHourOfInterval(
          {
            start: startOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
            end: endOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
          },
          { step: 4 },
        ).map((date, rowIndex) => {
          const rowSlices = slices.filter((slice) => slice.date.getHours() === date.getHours());

          return (
            <tr key={`row-${device.id}-${date.toISOString()}`} className="group/last-row">
              <th
                key={`slice-time-${device.id}-${date.toISOString()}`}
                className={clsx(
                  'sticky left-0 z-30 min-w-24 border-0 border-b border-solid bg-white pl-2 text-start text-sm font-normal leading-4',
                  deviceProducts.length >= 2
                    ? 'border-blue-grey-100'
                    : 'border-grey-100 group-last-of-type/last-row:border-blue-grey-100',
                )}
              >
                {`${date.toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                })} - ${addHours(date, 4).toLocaleTimeString(locale, {
                  hour: '2-digit',
                  minute: '2-digit',
                  hourCycle: 'h24',
                })}`}
              </th>
              <th className="sticky left-[calc(6rem+9px)] z-30 p-0">
                <div className="flex flex-col">
                  {deviceProducts.map((product, productIndex) => {
                    const oddProductsOffset = deviceProducts.length % 2 ? rowIndex : 0;
                    return (
                      <div
                        key={product}
                        className={clsx(
                          'min-w-[calc(4rem-2px)] border-0 border-b border-r border-solid px-1 text-start text-sm font-normal leading-6',
                          deviceProducts.length >= 2 && productIndex === deviceProducts.length - 1
                            ? 'border-blue-b-grey-100 border-r-grey-100'
                            : 'border-grey-100',

                          (productIndex + oddProductsOffset) % 2 === 0 ? 'bg-grey-50' : 'bg-white',
                          {
                            'group-last-of-type/last-row:border-b-blue-grey-100 ':
                              deviceProducts.length < 2 && productIndex === deviceProducts.length - 1,
                          },
                        )}
                      >
                        {t(`SHARED_PRODUCT_${product}`)}
                      </div>
                    );
                  })}
                </div>
              </th>

              {rowSlices.map((slice) => {
                const values = new Map(
                  slice.fields.map((field) => [
                    field.type,
                    convertMeasure(field.value, 'kW').value?.toLocaleString(locale, { useGrouping: false }) ?? '',
                  ]),
                );

                return (
                  <td
                    key={`slice-${device.id}-${slice.id}`}
                    className="relative snap-start scroll-ml-16 p-0 first:snap-none"
                  >
                    <div className="absolute inset-0 flex flex-col">
                      {slice.hasDowntime ? (
                        <div className="m-0 flex w-full items-center justify-center border-none bg-transparent p-0">
                          <WarningIcon className="text-black/70" />
                        </div>
                      ) : (
                        deviceProducts.map((product, productIndex) => (
                          <div
                            key={product}
                            className={clsx(
                              'group/cell relative box-border flex-auto border-0 border-b border-r border-solid text-center text-sm',
                              deviceProducts.length >= 2 && productIndex === deviceProducts.length - 1
                                ? 'border-b-blue-grey-100 border-r-grey-100'
                                : 'border-grey-100',
                              {
                                'bg-grey-50': (productIndex + (deviceProducts.length % 2 ? rowIndex : 0)) % 2 === 0,
                                'group-last-of-type/last-row:border-blue-grey-100':
                                  deviceProducts.length < 2 && productIndex === deviceProducts.length - 1,
                              },
                            )}
                          >
                            {values.get(product)}
                            <div
                              className={clsx('absolute inset-0', {
                                'bg-blue-grey-200/5': isWeekend(
                                  subHours(slice.date, slice.date.getTimezoneOffset() / 60),
                                ),
                              })}
                            />
                          </div>
                        ))
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
